(ns yardwerkz.web.jobs.detail
  "Job detail page: status, quotes, actions, modals, chat link."
  (:require
   [uix.core :refer [defui $ use-state use-callback]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.realtime :as realtime]
   [yardwerkz.web.router :as router]
   [yardwerkz.web.domain.jobs :as jobs]
   [yardwerkz.web.domain.quote :as quote]
   [yardwerkz.web.ui.layout :as layout]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]
   [yardwerkz.web.jobs.chat :as chat]))

;; =============================================================================
;; Status Banner
;; =============================================================================

(def ^:private status-colors
  {:green  "bg-green-100 text-green-800"
   :blue   "bg-blue-100 text-blue-800"
   :amber  "bg-amber-100 text-amber-800"
   :red    "bg-red-100 text-red-800"
   :violet "bg-violet-100 text-violet-800"
   :gray   "bg-gray-100 text-gray-700"})

(defn- status-class [status]
  (let [{:keys [color-key]} (jobs/get-status-config status)
        color (first color-key)]
    (get status-colors color "bg-gray-100 text-gray-700")))

(defui status-banner [{:keys [status]}]
  (let [{:keys [label]} (jobs/get-status-config status)]
    ($ :div {:class (str "flex items-center justify-center py-3 px-4 rounded-lg font-medium text-sm "
                         (status-class status))}
       ($ :span label))))

;; =============================================================================
;; Detail Section
;; =============================================================================

(defui detail-row [{:keys [icon label value]}]
  ($ :div {:class "flex items-start space-x-3 py-3 border-b border-gray-100 last:border-0"}
     ($ :div {:class "mt-0.5 text-gray-400"} icon)
     ($ :div {:class "flex-1"}
        ($ :p {:class "text-xs text-gray-500"} label)
        ($ :p {:class "text-sm font-medium text-gray-900"} value))))

;; =============================================================================
;; Quote Card
;; =============================================================================

(defui quote-card [{:keys [q selected? on-select]}]
  (let [card-data (quote/build-quote-card-data q)]
    ($ :button {:class (str "w-full text-left p-4 rounded-xl border-2 transition-all mb-3 "
                            (if selected?
                              "border-green-500 bg-green-50"
                              "border-gray-200 hover:border-green-300"))
                :on-click on-select}
       ;; Worker info
       ($ :div {:class "flex items-center mb-3"}
          ($ :div {:class "w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center mr-3"}
             ($ icons/User {:size 20 :class "text-blue-600"}))
          ($ :div {:class "flex-1"}
             ($ :p {:class "font-semibold text-gray-900"} (:worker-name card-data))
             (when (:has-rating? card-data)
               ($ :div {:class "flex items-center text-sm text-gray-500"}
                  ($ icons/Star {:size 14 :class "text-amber-500 mr-1"})
                  ($ :span (:worker-rating card-data))))))
       ;; Price + date
       ($ :div {:class "flex items-center justify-between pt-3 border-t border-gray-100"}
          ($ :span {:class "text-xl font-bold text-green-600"} (:price-display card-data))
          ($ :div {:class "text-right"}
             ($ :span {:class "text-xs text-gray-400"} (:date-display card-data))
             (when (:expiration-display card-data)
               ($ :p {:class "text-xs text-amber-600 mt-0.5"} (:expiration-display card-data))))))))

;; =============================================================================
;; Quotes Panel
;; =============================================================================

(defui quotes-panel [{:keys [job-id quotes-data on-quote-accepted]}]
  (let [dispatch (rfx/use-dispatch)
        quotes (or (:data quotes-data) [])
        loading? (:loading quotes-data)
        [selected set-selected] (use-state nil)
        [accepting set-accepting] (use-state false)
        summary (quote/build-quotes-summary quotes)
        actionable (quote/filter-actionable quotes)]

    ($ ui/card {:class "p-6"}
       ($ :div {:class "flex items-center justify-between mb-4"}
          ($ :h3 {:class "text-lg font-semibold text-gray-900"} "Quotes")
          (when (:price-range summary)
            ($ :span {:class "text-sm text-gray-500"} (:price-range summary))))

       (if loading?
         ($ :div {:class "flex justify-center py-8"}
            ($ ui/spinner {:size :sm}))
         (if (seq actionable)
           ($ :div
              ;; Quote list
              (for [q actionable]
                ($ quote-card {:key (:id q)
                               :q q
                               :selected? (= (:id q) selected)
                               :on-select #(set-selected (:id q))}))
              ;; Accept button
              (when selected
                (let [sel-quote (first (filter #(= (:id %) selected) quotes))]
                  ($ :div {:class "mt-4 space-y-3"}
                     ($ ui/button {:on-click (fn []
                                               (set-accepting true)
                                               (dispatch [::store/accept-quote
                                                          {:quote-id selected
                                                           :on-success (fn [_]
                                                                         (set-accepting false)
                                                                         (set-selected nil)
                                                                         (when on-quote-accepted (on-quote-accepted)))
                                                           :on-failure (fn [_]
                                                                         (set-accepting false)
                                                                         (ui/show-toast! :error "Failed to accept quote"))}]))
                                   :loading accepting
                                   :disabled (not (quote/can-accept? sel-quote))}
                        (str "Accept Quote — " (quote/format-price (:amount sel-quote))))
                     ($ ui/button {:variant :ghost
                                   :on-click (fn []
                                               (dispatch [::store/decline-quote
                                                          {:quote-id selected
                                                           :on-success (fn [_]
                                                                         (set-selected nil)
                                                                         (dispatch [::store/fetch-quotes {:job-id job-id}]))
                                                           :on-failure (fn [_]
                                                                         (ui/show-toast! :error "Failed to decline quote"))}]))}
                        "Decline Quote")))))
           ($ :div {:class "text-center py-6 text-gray-400"}
              ($ icons/Clock {:size 32 :class "mx-auto mb-2"})
              ($ :p "Waiting for quotes from pros...")))))))

;; =============================================================================
;; Cancel Modal
;; =============================================================================

(defui cancel-modal [{:keys [visible job-id on-close on-cancelled]}]
  (let [dispatch (rfx/use-dispatch)
        [reason set-reason] (use-state nil)
        [other-text set-other-text] (use-state "")
        [submitting set-submitting] (use-state false)
        can-submit? (jobs/can-submit-cancellation? reason other-text)]

    ($ ui/modal {:open visible :on-close on-close :title "Cancel Job"}
       ($ :div {:class "space-y-3"}
          ($ :p {:class "text-sm text-gray-500 mb-4"}
             "We're sorry to see you go. Please let us know why:")
          (for [{:keys [id label]} jobs/cancellation-reasons]
            ($ :button {:key id
                        :class (str "w-full text-left p-3 rounded-lg border-2 transition-all "
                                    (if (= reason id)
                                      "border-red-500 bg-red-50"
                                      "border-gray-200 hover:border-gray-300"))
                        :on-click #(set-reason id)}
               ($ :span {:class (if (= reason id) "text-red-800 font-medium" "text-gray-700")}
                  label)))
          (when (= reason :other)
            ($ ui/textarea {:value other-text
                            :on-change set-other-text
                            :placeholder "Tell us more..."
                            :rows 3}))
          ($ :div {:class "flex space-x-3 pt-4"}
             ($ ui/button {:variant :secondary :on-click on-close :class "flex-1"}
                "Keep Job")
             ($ ui/button {:variant :danger
                           :class "flex-1"
                           :disabled (not can-submit?)
                           :loading submitting
                           :on-click (fn []
                                       (set-submitting true)
                                       (let [reason-text (if (= reason :other) other-text (name reason))]
                                         (dispatch [::store/cancel-job
                                                    {:job-id job-id
                                                     :reason reason-text
                                                     :on-success (fn [_]
                                                                   (set-submitting false)
                                                                   (on-close)
                                                                   (when on-cancelled (on-cancelled)))
                                                     :on-failure (fn [_]
                                                                   (set-submitting false)
                                                                   (ui/show-toast! :error "Cancel failed"))}])))}
                "Cancel Job"))))))

;; =============================================================================
;; Approve Work Modal
;; =============================================================================

(defui approve-modal [{:keys [visible job-id on-close on-approved]}]
  (let [dispatch (rfx/use-dispatch)
        [submitting set-submitting] (use-state false)]
    ($ ui/modal {:open visible :on-close on-close :title "Approve Work"}
       ($ :div {:class "space-y-4"}
          ($ :p {:class "text-sm text-gray-600"}
             "Review the completed work. Once approved, the job will be marked complete and payment will be released.")
          ($ :div {:class "flex space-x-3 pt-2"}
             ($ ui/button {:variant :secondary :on-click on-close :class "flex-1"} "Not Yet")
             ($ ui/button {:class "flex-1"
                           :loading submitting
                           :on-click (fn []
                                       (set-submitting true)
                                       (dispatch [::store/approve-work
                                                  {:job-id job-id
                                                   :on-success (fn [_]
                                                                 (set-submitting false)
                                                                 (on-close)
                                                                 (when on-approved (on-approved))
                                                                 (ui/show-toast! :success "Work approved!"))
                                                   :on-failure (fn [_]
                                                                 (set-submitting false)
                                                                 (ui/show-toast! :error "Approval failed"))}]))}
                "Approve Work"))))))

;; =============================================================================
;; Rating Modal
;; =============================================================================

(defui rating-modal [{:keys [visible job-id worker-name on-close on-rated]}]
  (let [dispatch (rfx/use-dispatch)
        [rating set-rating] (use-state 0)
        [review set-review] (use-state "")
        [submitting set-submitting] (use-state false)]
    ($ ui/modal {:open visible :on-close on-close :title "Rate Your Pro"}
       ($ :div {:class "space-y-4"}
          (when worker-name
            ($ :p {:class "text-sm text-gray-500"} (str "How was your experience with " worker-name "?")))
          ;; Star rating
          ($ :div {:class "flex justify-center space-x-2"}
             (for [i (range 1 6)]
               ($ :button {:key i
                           :class "p-1 transition-transform hover:scale-110"
                           :on-click #(set-rating i)}
                  ($ icons/Star {:size 32
                                 :class (if (<= i rating)
                                          "text-amber-500 fill-amber-500"
                                          "text-gray-300")}))))
          ;; Review text
          ($ ui/textarea {:value review
                          :on-change set-review
                          :placeholder "Leave a review (optional)..."
                          :rows 3})
          ;; Submit
          ($ ui/button {:disabled (zero? rating)
                        :loading submitting
                        :class "w-full"
                        :on-click (fn []
                                    (set-submitting true)
                                    (dispatch [::store/rate-job
                                               {:job-id job-id
                                                :rating rating
                                                :review review
                                                :on-success (fn [_]
                                                              (set-submitting false)
                                                              (on-close)
                                                              (when on-rated (on-rated))
                                                              (ui/show-toast! :success "Thanks for your review!"))
                                                :on-failure (fn [_]
                                                              (set-submitting false)
                                                              (ui/show-toast! :error "Rating failed"))}]))}
             "Submit Rating")))))

;; =============================================================================
;; Dispute Modal
;; =============================================================================

(defui dispute-modal [{:keys [visible job-id on-close on-disputed]}]
  (let [dispatch (rfx/use-dispatch)
        [reason set-reason] (use-state "")
        [submitting set-submitting] (use-state false)]
    ($ ui/modal {:open visible :on-close on-close :title "Report an Issue"}
       ($ :div {:class "space-y-4"}
          ($ :p {:class "text-sm text-gray-500"}
             "Describe the issue and we'll review it promptly.")
          ($ ui/textarea {:value reason
                          :on-change set-reason
                          :placeholder "Describe the issue..."
                          :rows 4})
          ($ :div {:class "flex space-x-3 pt-2"}
             ($ ui/button {:variant :secondary :on-click on-close :class "flex-1"} "Cancel")
             ($ ui/button {:variant :danger
                           :class "flex-1"
                           :disabled (empty? reason)
                           :loading submitting
                           :on-click (fn []
                                       (set-submitting true)
                                       (dispatch [::store/dispute-job
                                                  {:job-id job-id
                                                   :reason reason
                                                   :on-success (fn [_]
                                                                 (set-submitting false)
                                                                 (on-close)
                                                                 (when on-disputed (on-disputed))
                                                                 (ui/show-toast! :success "Issue reported"))
                                                   :on-failure (fn [_]
                                                                 (set-submitting false)
                                                                 (ui/show-toast! :error "Report failed"))}]))}
                "Submit Report"))))))

;; =============================================================================
;; Job Detail Page
;; =============================================================================

(defui detail-page [{:keys [route-match]}]
  (let [job-id (some-> route-match :parameters :path :id)
        dispatch (rfx/use-dispatch)
        {:keys [data loading?]} (realtime/use-job job-id)
        job-cancelling? (rfx/use-sub [::store/job-cancelling])
        quotes-data (rfx/use-sub [::store/job-quotes job-id])

        ;; Transform for display
        transformed (when data (jobs/transform-job data))
        {:keys [human-id service address date time price worker status]} (or transformed {})

        ;; Status checks
        has-quotes? (jobs/has-quotes? status nil)
        can-cancel? (and (jobs/can-cancel? status (jobs/cancelled? status))
                         (not job-cancelling?))
        can-approve? (jobs/can-approve? status (some? worker))
        can-rate? (jobs/can-rate? status (some? worker) (:has-customer-rating? transformed))
        can-dispute? (and (contains? #{:started :in-progress :finished :completed} status)
                          (not (contains? #{:disputed :resolved} status)))

        ;; Modal state
        [active-modal set-modal] (use-state nil)
        close-modal! (use-callback #(set-modal nil) [])

        ;; Show chat drawer
        [show-chat? set-show-chat] (use-state false)

        ;; Fetch quotes when status is :quoted
        _ (when (and has-quotes? (nil? quotes-data))
            (dispatch [::store/fetch-quotes {:job-id job-id}]))]

    ($ layout/page-container
       ;; Back button
       ($ :button {:class "flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
                   :on-click #(router/navigate! :jobs)}
          ($ icons/ChevronLeft {:size 16})
          ($ :span "Back to Jobs"))

       (if loading?
         ($ :div {:class "flex justify-center py-12"}
            ($ ui/spinner))
         (if data
           ($ :div {:class "space-y-6"}
              ;; Header
              ($ :div
                 ($ :h1 {:class "text-2xl font-bold text-gray-900"}
                    (or service "Job Detail"))
                 (when human-id
                   ($ :p {:class "text-sm text-gray-400 mt-1"} human-id)))

              ;; Status banner
              ($ status-banner {:status status})

              ;; Dispute banner
              (when (contains? #{:disputed :resolved} status)
                (let [is-disputed? (= status :disputed)
                      dispute-reason (or (:job/dispute-reason data) (:dispute-reason data))]
                  ($ :div {:class (str "p-4 rounded-lg border "
                                       (if is-disputed?
                                         "bg-red-50 border-red-200"
                                         "bg-green-50 border-green-200"))}
                     ($ :p {:class (str "font-semibold "
                                        (if is-disputed? "text-red-800" "text-green-800"))}
                        (if is-disputed? "Dispute Open" "Dispute Resolved"))
                     (when dispute-reason
                       ($ :p {:class (str "text-sm mt-1 "
                                          (if is-disputed? "text-red-600" "text-green-600"))}
                          dispute-reason)))))

              ;; Details card
              ($ ui/card {:class "p-4 divide-y divide-gray-100"}
                 ($ detail-row {:icon ($ icons/MapPin {:size 18})
                                :label "Location"
                                :value (or address "Not set")})
                 ($ detail-row {:icon ($ icons/Calendar {:size 18})
                                :label "Schedule"
                                :value (cond
                                         (and date time) (str date " \u2022 " time)
                                         date date
                                         time time
                                         :else "Not scheduled")})
                 ($ detail-row {:icon ($ icons/DollarSign {:size 18})
                                :label "Price"
                                :value (or price "Quote pending")})
                 (when worker
                   ($ detail-row {:icon ($ icons/User {:size 18})
                                  :label "Assigned Pro"
                                  :value (:name worker)})))

              ;; Quotes panel (when status is quoted)
              (when has-quotes?
                ($ quotes-panel {:job-id job-id
                                 :quotes-data quotes-data
                                 :on-quote-accepted #(ui/show-toast! :success "Quote accepted!")}))

              ;; Action buttons
              ($ :div {:class "space-y-3"}
                 ;; Approve Work
                 (when can-approve?
                   ($ ui/button {:class "w-full"
                                 :on-click #(set-modal :approve)}
                      ($ :span {:class "flex items-center justify-center space-x-2"}
                         ($ icons/CheckCircle {:size 18})
                         ($ :span "Approve Work"))))

                 ;; Rate Worker
                 (when can-rate?
                   ($ ui/button {:class "w-full"
                                 :on-click #(set-modal :rating)}
                      ($ :span {:class "flex items-center justify-center space-x-2"}
                         ($ icons/Star {:size 18})
                         ($ :span "Rate Worker"))))

                 ;; Report Issue
                 (when can-dispute?
                   ($ ui/button {:variant :ghost :class "w-full text-red-600 hover:bg-red-50"
                                 :on-click #(set-modal :dispute)}
                      ($ :span {:class "flex items-center justify-center space-x-2"}
                         ($ icons/AlertCircle {:size 18})
                         ($ :span "Report Issue"))))

                 ;; Cancel Job
                 (when can-cancel?
                   ($ ui/button {:variant :ghost :class "w-full text-red-600 hover:bg-red-50"
                                 :on-click #(set-modal :cancel)}
                      ($ :span {:class "flex items-center justify-center space-x-2"}
                         ($ icons/X {:size 18})
                         ($ :span "Cancel Job"))))

                 ;; Message / Chat toggle
                 ($ ui/button {:variant :secondary :class "w-full"
                               :on-click #(set-show-chat (not show-chat?))}
                    ($ :span {:class "flex items-center justify-center space-x-2"}
                       ($ icons/MessageSquare {:size 18})
                       ($ :span (if show-chat? "Hide Chat" "Message Worker")))))

              ;; Inline chat panel
              (when show-chat?
                ($ ui/card {:class "h-96 overflow-hidden"}
                   ($ chat/chat-panel {:job-id job-id})))

              ;; Modals
              ($ cancel-modal {:visible (= active-modal :cancel)
                               :job-id job-id
                               :on-close close-modal!
                               :on-cancelled #(router/navigate! :jobs)})
              ($ approve-modal {:visible (= active-modal :approve)
                                :job-id job-id
                                :on-close close-modal!})
              ($ rating-modal {:visible (= active-modal :rating)
                               :job-id job-id
                               :worker-name (when worker (:name worker))
                               :on-close close-modal!})
              ($ dispute-modal {:visible (= active-modal :dispute)
                                :job-id job-id
                                :on-close close-modal!}))

           ;; Job not found
           ($ ui/empty-state
              {:title "Job not found"
               :description "This job may have been removed."}))))))
